import WebApp from "@twa-dev/sdk";

/**
 * One-time TMA bootstrap. Idempotent — safe to call from main.tsx.
 *
 * Responsibilities (per .claude/rules/frontend.md):
 *   - hide Telegram's loading placeholder (`ready()`) and go full screen (`expand()`)
 *   - apply combined safeAreaInset + contentSafeAreaInset as CSS vars
 *   - update CSS vars when theme or viewport changes
 *   - return the parsed startapp deep-link param for the router
 */
export function bootstrapTelegram(): { startParam?: string } {
  // Some browsers / dev environments don't expose WebApp; guard so dev preview still loads.
  if (typeof WebApp === "undefined" || !WebApp.initData) {
    return {};
  }

  WebApp.ready();
  WebApp.expand();

  applyInsets();
  // Some events ('safeAreaChanged', 'contentSafeAreaChanged', 'activated') exist on
  // recent Telegram clients but aren't in @twa-dev/sdk's EventNames union yet. Cast
  // the call sites; the SDK forwards any event name to Telegram regardless.
  const on = WebApp.onEvent as unknown as (e: string, cb: (...args: unknown[]) => void) => void;
  on("safeAreaChanged", applyInsets);
  on("contentSafeAreaChanged", applyInsets);
  on("viewportChanged", ((e: { isStateStable?: boolean }) => {
    if (e.isStateStable) applyInsets();
  }) as (...args: unknown[]) => void);

  // Resume repaint — Telegram's Android webview can leave a stale frozen frame
  // when the TMA is reopened from minimised; the user has to scroll to force a
  // repaint. Re-apply the viewport and nudge the compositor on resume so the
  // live UI shows immediately. Covered via both Telegram's `activated` event and
  // the standard visibilitychange.
  on("activated", () => {
    applyInsets();
    forceRepaint();
  });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) forceRepaint();
  });

  const startParam = WebApp.initDataUnsafe?.start_param;
  return startParam ? { startParam } : {};
}

/** The raw `?startapp=` deep-link param, if any (e.g. `channel_<id>`). */
export function getStartParam(): string | undefined {
  return WebApp.initDataUnsafe?.start_param ?? undefined;
}

/**
 * Force the webview compositor to flush a frame without changing layout. Toggles
 * a no-op transform on #root across a frame so a stale post-resume frame repaints.
 */
export function forceRepaint(): void {
  const el = document.getElementById("root");
  if (!el) return;
  el.style.transform = "translateZ(0)";
  void el.offsetHeight; // force reflow
  requestAnimationFrame(() => {
    el.style.transform = "";
  });
}

/* ---------------------------------------------------------------------------
 * Native interaction helpers (frontend.md). All guard against a missing SDK so
 * the browser dev preview never throws.
 * ------------------------------------------------------------------------- */

type ImpactStyle = "light" | "medium" | "heavy" | "rigid" | "soft";
type NotificationType = "error" | "success" | "warning";

export const haptics = {
  impact(style: ImpactStyle = "medium"): void {
    WebApp.HapticFeedback?.impactOccurred?.(style);
  },
  notify(type: NotificationType): void {
    WebApp.HapticFeedback?.notificationOccurred?.(type);
  },
  select(): void {
    WebApp.HapticFeedback?.selectionChanged?.();
  },
};

/** Open the original Telegram message. Uses the native in-app browser/deep link. */
export function openTelegramLink(url: string): void {
  if (WebApp.openTelegramLink) WebApp.openTelegramLink(url);
  else window.open(url, "_blank");
}

/**
 * Bottom-sheet swipe guards. Telegram's own swipe-to-close otherwise intercepts a
 * sheet's drag-to-dismiss and closes the WHOLE TMA (frontend.md hard rule). Call
 * `lock` when a sheet opens, `unlock` when it closes.
 */
export const verticalSwipes = {
  lock(): void {
    (WebApp as unknown as { disableVerticalSwipes?: () => void }).disableVerticalSwipes?.();
  },
  unlock(): void {
    (WebApp as unknown as { enableVerticalSwipes?: () => void }).enableVerticalSwipes?.();
  },
};

/** Dismiss the keyboard (Bot API 9.1+) — e.g. after submitting a search. */
export function hideKeyboard(): void {
  (WebApp as unknown as { hideKeyboard?: () => void }).hideKeyboard?.();
}

const backButton = WebApp.BackButton as unknown as
  | { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void }
  | undefined;

/**
 * Sync the native Telegram BackButton with router depth. Show when there's
 * somewhere to go back to; wire its click to the supplied handler.
 */
export function syncBackButton(canGoBack: boolean, onBack: () => void): () => void {
  if (!backButton) return () => {};
  if (canGoBack) {
    backButton.onClick(onBack);
    backButton.show();
  } else {
    backButton.hide();
  }
  return () => backButton.offClick(onBack);
}

function applyInsets(): void {
  // Telegram exposes both — combine them so bottom nav + top bar clear ALL Telegram UI
  // and any device notch / home indicator.
  const sa = (WebApp as unknown as { safeAreaInset?: { top: number; bottom: number } }).safeAreaInset ?? { top: 0, bottom: 0 };
  const csa =
    (WebApp as unknown as { contentSafeAreaInset?: { top: number; bottom: number } }).contentSafeAreaInset ??
    { top: 0, bottom: 0 };
  const root = document.documentElement.style;
  root.setProperty("--safe-top", `${sa.top + csa.top}px`);
  root.setProperty("--safe-bottom", `${sa.bottom + csa.bottom}px`);
  root.setProperty("--viewport-height", `${WebApp.viewportStableHeight ?? window.innerHeight}px`);
}

export { WebApp };
